'use client';

import React, { useRef, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import styles from './banner-popup-modal.module.scss';
import {submitCollection} from '_services/submit.service';
import axios from 'axios';
import { cookingLoadingService } from '_services/loadingCooking.service';
import { timezoneDateConvert } from '_services/common.service';

interface BannerPopupModalProps {
  show: boolean;
  onHide: () => void;
  banners: PromoBanner[];
  showTitle?: boolean;
  timeZone?: string;
}

const BannerPopupModal: React.FC<BannerPopupModalProps> = ({ show, onHide, banners, showTitle = true, timeZone = '' }) => {
  const t = useTranslations();
  const router = useRouter();
  const swiperRef = useRef<SwiperClass | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<Record<string, boolean>>({});
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);


  if (!banners || banners.length === 0) return null;

  const handleButtonClick = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    onHide();
  };

  const isOfferValidToday = (banner: PromoBanner) => {
    if (!banner.popupStartDate || !banner.popupEndDate) return false;
    const now = new Date();
    const startDate = new Date(timezoneDateConvert({ 
      utcDate: (banner.popupStartDate || ''), 
      timezone: timeZone, 
    }));
    const endDate = new Date(timezoneDateConvert({ 
      utcDate: (banner.popupEndDate || ''), 
      timezone: timeZone, 
    }));
    return now >= startDate && now <= endDate;
  };

  const startAutoTimer = (slideIndex: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const currentBanner = banners[slideIndex];
    const viewTime = currentBanner?.popupViewTimeSec || 30;

    timeoutRef.current = setTimeout(() => {
      if (banners.length === 1) {
        // Single banner, just close
        onHide();
      } else {
        // Multiple banners
        if (slideIndex === banners.length - 1) {
          // Last banner, close the modal
          onHide();
          // console.log("onhide");
        } else {
          // Go to next banner
          const nextIndex = slideIndex + 1;
          if (swiperRef.current) {
            swiperRef.current.slideTo(nextIndex);
          }
        }
      }
    }, viewTime * 1000);
  };

//   useEffect(() => {
//     if (show && banners.length > 0) {
//       setCurrentSlideIndex(0);
//       startAutoTimer(0);
//     } else {
//       // Clear timeout when modal is hidden
//       if (timeoutRef.current) {
//         clearTimeout(timeoutRef.current);
//         timeoutRef.current = null;
//       }
//     }

//     return () => {
//       if (timeoutRef.current) {
//         clearTimeout(timeoutRef.current);
//       }
//     };
//   }, [show, banners]);

  const handleSlideChange = (swiper: SwiperClass) => {
    const newIndex = swiper.realIndex;
    setCurrentSlideIndex(newIndex);
    startAutoTimer(newIndex);
  };

  // const handleShowData = async () => {
  //   // Call loadBannerItemsDetails API
  //   const currentBanner = banners[currentSlideIndex];
  //   if (currentBanner) {
  //     try {
  //       const response: CommonResponse | null = await submitHandler(
  //         'banners:loadBannerItemsDetails',
  //         submitCollection.loadBannerItemsDetails,
  //         { bannerId: currentBanner.bannerId },
  //         false,
  //         null
  //       );
  //       console.log('Banner Items Details Response:', response);
  //     } catch (error) {
  //       console.error('Error loading banner items details:', error);
  //     }
  //   }
  // };

  const handleShowData = async () => {
    if (isLoading) return;
    // Call loadBannerItemsDetails API
    const currentBanner = banners[currentSlideIndex];

    setIsLoading(true);
    // const promoBanners: CommonResponse | null = await submitHandler('banner:promoBanners', submitCollection.loadBanners, null, false, null);
    
    if (currentBanner) {
      try {
        const handlerId = `offers:details:${(currentBanner?.bannerId || "") || "unknown"}`;
        const respObj = await axios.post('/api/submit', {
          urlId: handlerId,
          callstat: submitCollection.loadBannerItemsDetails,
          cobj: {
            "bannerId": currentBanner.bannerId,
          },
          showalerts: false,
          options: null
        });
        
        // console.log('Banner Items Details Response:', respObj.data);
        // console.log('Banner Items Details Response test:', respObj.data.extra.items[0].itemType);

        // Check if response has items
        const items = respObj.data?.extra?.items || [];
        const itemType = respObj.data.extra.items[0].itemType || [];
        
        if (items.length === 1 && (itemType === 'ITEM')) {
          const item = items[0];
          const itemName = item.details?.itemName || item.itemId;
          const encodedItemName = encodeURIComponent(itemName);
          handleButtonClick(); // Close modal
          cookingLoadingService.loading(true);
          router.push(`/item/${encodedItemName}`);
        } else if (items.length === 1 && (itemType === 'DEAL_ITEM')) {
          const item = items[0];
          const itemName = item.details?.dealName;
          const encodedItemName = encodeURIComponent(itemName);
          handleButtonClick(); // Close modal
          cookingLoadingService.loading(true);
          router.push(`/combo/${encodedItemName}`);
        }else {
          handleButtonClick(); // Close modal
          cookingLoadingService.loading(true);
          router.push(`/menu/offers/${currentBanner.bannerId}`);
        }
      } catch (error) {
        console.error('Error loading banner items details:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleButtonClick}
      centered
      className={styles.bannerPopupModal}
      backdrop="static"
    >
      <Modal.Body className={styles.modalBody}>
        <div className={styles.header}>
          {showTitle && <span className={styles.commonTitle}>{t('bannerPopup.TITLE', { offerCount: banners.length })}</span>}
          <button type="button" className={styles.close} onClick={handleButtonClick} aria-label="Close">{t('bannerPopup.CLOSE') || 'Close'}</button>
        </div>
        <Swiper
          modules={[Navigation, Pagination]}
          navigation={false}
          pagination={{ clickable: true }}
          loop={false}
          allowTouchMove={!isLoading}
          allowSlideNext={!isLoading}
          allowSlidePrev={!isLoading}
          className={styles.modalSwiper}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            if (show && banners.length > 0) {
              swiper.slideTo(0);
            }
          }}
          onSlideChange={isLoading ? undefined : handleSlideChange}
          initialSlide={0}
        >
          {banners.map((banner) => (
            <SwiperSlide key={banner.bannerId}>
              <div className={styles.imageContainer}>
                {!imagesLoaded[banner.bannerId] && (
                  <div className={styles.imageSkeleton} />
                )}
                <Image
                  src={banner.imageUrl}
                  alt={banner.title}
                  width={600}
                  height={350}
                  layout="responsive"
                  objectFit="cover"
                  className={`${styles.bannerImage} ${!imagesLoaded[banner.bannerId] ? styles.imageHidden : ''}`}
                  onLoad={() => setImagesLoaded(prev => ({ ...prev, [banner.bannerId]: true }))}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/placeholder.png';
                    setImagesLoaded(prev => ({ ...prev, [banner.bannerId]: true }));
                  }}
                />
              </div>
              <div className={styles.content}>
                <h3 className={styles.title}>{banner.title}</h3>
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{ __html: banner.description || '' }}
                />
                {/* {isOfferValidToday(banner) && (
                  <span className={styles.offerLabel}>{t('bannerPopup.offerValidToday') || 'Offer is valid for today'}</span>
                )} */}
                {banner.bannerItems && banner.bannerItems.length > 0 && isOfferValidToday(banner) && (
                  <Button
                    variant="primary"
                    onClick={handleShowData}
                    className={styles.actionButton}
                    disabled={isLoading}
                  >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" /> {t('bannerPopup.loading') || 'Loading...'}
                    </>
                  ) : (
                    t('bannerPopup.viewDetails') || 'Order Now'
                  )}
                </Button>)}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </Modal.Body>
    </Modal>
  );
};

export default BannerPopupModal;